/**
 * Fire-and-forget first-party funnel tracking.
 *
 * Sends the event to /api/analytics/event (persisted into system_events as
 * `funnel.<event>`) and mirrors it into PostHog when the SDK is loaded
 * (window.posthog is set by PostHogProvider). Never throws, never blocks —
 * analytics must not affect product flows.
 */

export type FunnelEvent =
  | 'auth_modal_opened'
  | 'signup_submitted'
  | 'oauth_clicked'
  | 'otp_verified'
  | 'character_card_clicked'
  | 'buy_coins_opened'
  | 'checkout_started'
  // Chat activation — the yardstick for whether people who reach a chat
  // actually converse (previously a total blind spot).
  | 'message_sent'
  | 'first_reply_received'
  // Guest (logged-out) free-chat funnel.
  | 'guest_message_sent'
  | 'guest_limit_reached'
  // Daily free-message cap for signed-in free users (admin-tunable).
  | 'free_limit_reached'
  // /match ad-lander configurator funnel.
  | 'match_quiz_started'
  | 'match_step_completed'
  | 'match_revealed'
  | 'match_chat_clicked'
  // /live Live Action demo — which actions drive signups.
  | 'live_action_viewed'
  | 'live_action_played'
  | 'live_action_locked_clicked';

export type FunnelEventData = Record<string, string | number | boolean>;

export function trackFunnelEvent(event: FunnelEvent, data?: FunnelEventData) {
  if (typeof window === 'undefined') return;

  try {
    void fetch('/api/analytics/event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event, data }),
      // Let the request survive page navigations (e.g. character card clicks).
      keepalive: true,
    }).catch(() => {
      // Swallow — tracking must never surface errors.
    });

    const posthog = (
      window as Window & {
        posthog?: { capture: (event: string, data?: FunnelEventData) => void };
      }
    ).posthog;

    posthog?.capture(event, data);
  } catch {
    // Swallow — tracking must never surface errors.
  }
}
